import React from 'react'
import {classNames} from '../../../common/util/util-helpers'

/**
 * Shared vocabulary for the two reports built on accounting/reports/inventory-exceptions:
 * Inventory velocity (Mode=velocity, days) and Inventory aging (Mode=aging, months).
 *
 * They read the same rows from the same endpoint, so most of the column text is identical.
 * It lives here once. Each tab composes its own glossary from its own column list via
 * helpFor(), which means the glossary can never list a column the grid does not show, or
 * miss one it does.
 *
 * End-user documentation lives in the backend repo at docs/INV_TRACKING.md.
 */

export const SIGNAL_LABELS = {
    NEVER_SOLD: 'Never sold',
    WO_NEVER_BILLED: 'On an unbilled work order',
    WO_CONSUMED_NO_SALE: 'Fitted but never sold',
    REPEAT_PURCHASE: 'Bought repeatedly',
    SINGLE_PURCHASE: 'Bought once',
    STILL_BUYING_NOT_SELLING: 'Still buying, not selling'
}

export const VALUATION_NOTE = 'Quantities match the inventory screen exactly. Values use the average price you have paid over time, so they sit close to - but not exactly on - the balance sheet figure.'

/**
 * Keyed by field name, so helpFor() can follow a grid's real column order. Entries that
 * only one report shows (OverdueDays, Category, the two "days since" clocks) sit here too;
 * a report simply never asks for the ones it does not render.
 */
export const COLUMN_HELP = {
    ProductService: ['Product/Service', 'the part, named as it is on the parts screen.'],
    SKU: ['SKU', 'your own part number for it.'],
    Category: ['Category', 'which of the two problems this is. "Never sold" means you have never billed one to anybody. "Stale" means it used to sell and then stopped.'],
    LikelyCause: ['Likely cause', 'the single headline reason this part is listed. A part can trip more than one test; the most expensive one wins, which is why a billing error outranks slow stock.'],
    OverdueDays: ['Overdue', 'days past your expected turn time, counted from the last purchase. A part showing 5 d was bought 5 days longer ago than you said stock should sit.'],
    OnHandQty: ['On hand', 'units on the shelf right now. This matches the inventory screen exactly.'],
    OnHandValue: ['Value on hand', 'On hand multiplied by Unit cost. This is the money standing still.'],
    UnitCost: ['Unit cost', 'average purchase cost per unit: everything you have ever spent on the part divided by every unit you have ever bought.'],
    LastPurchaseDate: ['Last purchased', 'the most recent purchase.'],
    DaysSinceLastPurchase: ['Since purchase', 'how long it has been since you last bought one. A long gap here with stock still on the shelf means you stopped restocking it, but never used up what you had.'],
    PurchaseEvents: ['Times bought', 'how many separate bills the part appears on. Buying it over and over while never selling it is the signature of a shop supply filed as inventory.'],
    SellQty: ['Qty sold', "units invoiced to a customer over the part's whole history."],
    LastSaleDate: ['Last sold', 'the most recent invoice. "Never" means it has never been billed to anyone.'],
    DaysSinceLastSale: ['Since sale', 'how long it has been since one was last billed to a customer. This is the clock the stale test runs on. Blank means it has never sold.'],
    WorkOrderQty: ['On work orders', 'units written onto work orders. Putting a part on a work order does not move stock on its own; stock moves when that work order is billed.'],
    WOQtyNeverBilled: ['Unbilled WO qty', 'of those, the units on work orders that were never invoiced. This is money you have already spent and not charged for, which is why it shows in red.'],
    DaysOfSupply: ['Days of supply', 'how long the stock would last at the rate it has historically left. "No sales" means nothing has ever left, so there is no rate to project from - that is a different statement to "a very long time".'],
    Signals: ['Signals', 'the supporting evidence behind the cause. A part can carry several.']
}

/**
 * The only fields the endpoint can sort by -- the whitelist in
 * InventoryExceptionsManager::sort(). Sorting happens in PHP over the whole filtered set,
 * and anything outside this list is silently ignored there, so those headers must not
 * offer a sort control: clicking one would look like it worked and change nothing.
 *
 * Keep this in step with that whitelist. Adding a field here without adding it there
 * brings the dead header back.
 */
export const SORTABLE_COLUMNS = [
    'ProductService',
    'LikelyCause',
    'OnHandQty',
    'OnHandValue',
    'OverdueDays',
    'PurchaseEvents',
    'DaysOfSupply',
    'DaysSinceLastPurchase',
    'DaysSinceLastSale'
]

/**
 * Marks every column the server cannot sort by as omitSort, in place. ResourceTable reads
 * omitSort from the live field metadata rather than from saved table options, so this
 * takes effect for existing users without retiring their stored column layout.
 */
export function withSortDisabled(fields) {
    Object.keys(fields).forEach((name) => {
        if (!SORTABLE_COLUMNS.includes(name)) {
            fields[name].metadata = Object.assign({}, fields[name].metadata, {omitSort: true})
        }
    })

    return fields
}

/** Glossary entries for the given field names, in the order given, skipping unknowns. */
export function helpFor(names) {
    return names.map((name) => COLUMN_HELP[name]).filter(Boolean)
}

export function HelpList({items, addClass}) {
    return (
        <ul className={classNames('space-y-0.5', addClass)}>
            {items.map(([term, text]) => (
                <li key={term} className="break-inside-avoid">
                    <span className="font-medium">{term}</span> - {text}
                </li>
            ))}
        </ul>
    )
}

/**
 * The expandable panel behind the info icon. Both reports use the same frame and pass
 * their own intro, settings and causes; the column glossary is derived from their grid.
 */
export function ReportHelpPanel({title, intro, settings, causes, columns, onClose}) {
    return (
        <div className="mx-4 mb-4 p-4 rounded-card bg-inverse border border-tm-gray-300 text-sm">
            <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="font-semibold">{title}</h3>

                <button className="btn btn-text font-semibold uppercase text-xs shrink-0" onClick={onClose}>
                    Hide
                </button>
            </div>

            <p className="mb-4 text-tm-gray-700">{intro}</p>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-4">
                <div>
                    <p className="font-semibold mb-1">The settings</p>
                    <HelpList items={settings}/>
                </div>

                <div>
                    <p className="font-semibold mb-1">What the causes mean</p>
                    <HelpList items={causes}/>
                </div>
            </div>

            <p className="font-semibold mb-1">What each column means</p>
            {/* CSS columns rather than a grid: the entries are different heights, and this
                keeps them flowing tightly instead of leaving ragged gaps. */}
            <HelpList items={columns} addClass="sm:columns-2 gap-x-8"/>

            <p className="mt-4 text-tm-gray-700">{VALUATION_NOTE}</p>
        </div>
    )
}
